import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Loader2, LogIn, LogOut, Info, AlertTriangle } from "lucide-react";

export default function CheckIn() {
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, checked_in, checked_out, already_checked_out, error, auth
  const [message, setMessage] = useState("");
  const [delayMinutes, setDelayMinutes] = useState(null);

  const jobId = params.get("job");
  const token = params.get("token");

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setStatus("auth");
      return;
    }

    if (!jobId || !token) {
      setStatus("error");
      setMessage("Invalid QR code. Please ask the manager for a new one.");
      return;
    }

 const checkin = async () => {
      try {
        const { data } = await api.post(`/jobs/${jobId}/qr/checkin`, { token });
        setStatus(data.status || "checked_in");
        setMessage(data.message || "");
        setDelayMinutes(data.delay_minutes ?? null);
      } catch (e) {
        setStatus("error");
        setMessage(e.response?.data?.detail || "Check-in failed. Please try again.");
      }
    };

    checkin();
  // eslint-disable-next-line
  }, [user, authLoading]);

  if (status === "loading" || authLoading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="text-center">
        <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto" />
        <p className="mt-4 font-display text-xl">Checking you in/out…</p> 
        </div>
      </div>
    );
  }

  if (status === "auth") {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <div className="text-center max-w-sm">
          <p className="font-display text-2xl mb-2">Sign in to check in</p>
          <p className="text-sm text-muted-foreground mb-6">You need to be logged into your EarnMate account to check in for this event.</p>
          <Link
            to={`/login?redirect=/checkin?job=${jobId}&token=${token}`}
            className="btn-primary"
          >
            Sign in to EarnMate
          </Link>
        </div>
      </div>
    );
  }

 if (status === "checked_in") {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <div className="text-center max-w-sm">
          <LogIn className="w-16 h-16 text-brand mx-auto mb-4" />
          <p className="font-display text-2xl mb-2">Checked in ✓</p>
          {delayMinutes ? (
            <p className="text-sm font-semibold text-red-500 mb-2 flex items-center justify-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> Delayed by {delayMinutes} min
            </p>
          ) : (
            <p className="text-sm text-emerald-600 mb-2">On time</p>
          )}
          <p className="text-sm text-muted-foreground mb-6">Scan the same QR code again when you leave to check out and mark attendance complete.</p>
          <Link to="/dashboard" className="btn-primary">Go to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (status === "checked_out") {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <div className="text-center max-w-sm">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <p className="font-display text-2xl mb-2">Checked out ✓</p>
          <p className="text-sm text-muted-foreground mb-6">{message}</p>
          <Link to="/dashboard" className="btn-primary">Go to Dashboard</Link>
        </div>
      </div>
    );
  }

  if (status === "already_checked_out") {
    return (
      <div className="min-h-[60vh] grid place-items-center px-4">
        <div className="text-center max-w-sm">
          <Info className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <p className="font-display text-2xl mb-2">Already checked out</p>
          <p className="text-sm text-muted-foreground mb-6">{message}</p>
          <Link to="/dashboard" className="btn-outline">Go to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] grid place-items-center px-4">
      <div className="text-center max-w-sm">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <p className="font-display text-2xl mb-2">Check-in Failed</p>
        <p className="text-sm text-muted-foreground mb-6">{message}</p>
        <Link to="/dashboard" className="btn-outline">Go to Dashboard</Link>
      </div>
    </div>
  );
}
